import type { ReactNode } from 'react';
import {
  LogoutIcon,
  MenuIcon,
  MoonIcon,
  SidebarIcon,
  SunIcon,
} from '../components/ui';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { t } from '../i18n';

type AppHeaderProps = {
  title: string;
  onOpenMenu: () => void;
  onToggleSidebar: () => void;
  sidebarCollapsed: boolean;
};

function userInitials(name?: string | null, email?: string | null): string {
  const source = (name || email || '?').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toLocaleUpperCase('el-GR');
  }
  return source.slice(0, 2).toLocaleUpperCase('el-GR');
}

function HeaderIconButton({
  label,
  onClick,
  children,
  className = '',
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={[
        'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
        'text-ink-muted transition-colors',
        'hover:bg-surface-muted hover:text-ink',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-1 focus-visible:ring-offset-surface-card',
        className,
      ].join(' ')}
    >
      {children}
    </button>
  );
}

const iconClass = 'h-[18px] w-[18px]';

export function AppHeader({
  title,
  onOpenMenu,
  onToggleSidebar,
  sidebarCollapsed,
}: AppHeaderProps) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const labels = t().nav;

  return (
    <header className="relative z-20 flex h-14 shrink-0 items-center justify-between gap-3 border-b border-line bg-surface-card px-3 sm:px-5">
      <div className="flex min-w-0 items-center gap-1 sm:gap-2">
        <HeaderIconButton
          label={labels.openMenu}
          onClick={onOpenMenu}
          className="lg:hidden"
        >
          <MenuIcon className={iconClass} />
        </HeaderIconButton>

        <HeaderIconButton
          label={
            sidebarCollapsed ? labels.expandSidebar : labels.collapseSidebar
          }
          onClick={onToggleSidebar}
          className="hidden lg:inline-flex"
        >
          <SidebarIcon className={iconClass} />
        </HeaderIconButton>

        <div
          className="mx-1 hidden h-5 w-px bg-line sm:block"
          aria-hidden
        />

        <div className="min-w-0 px-1">
          <h1 className="truncate text-[15px] font-semibold leading-snug tracking-tight text-ink sm:text-base">
            {title}
          </h1>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
        <HeaderIconButton
          label={theme === 'dark' ? labels.themeLight : labels.themeDark}
          onClick={toggleTheme}
        >
          {theme === 'dark' ? (
            <SunIcon className={iconClass} />
          ) : (
            <MoonIcon className={iconClass} />
          )}
        </HeaderIconButton>

        <div
          className="mx-1.5 hidden h-5 w-px bg-line sm:block"
          aria-hidden
        />

        <div className="flex items-center gap-2.5 rounded-md px-1.5 py-1 sm:px-2">
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1e3a5f] text-[11px] font-semibold tracking-wide text-white dark:bg-sky-600"
            aria-hidden
          >
            {userInitials(user?.name, user?.email)}
          </div>
          <div className="hidden min-w-0 sm:block">
            <div className="truncate text-sm font-medium leading-tight text-ink">
              {user?.name}
            </div>
            <div className="truncate text-[11px] leading-tight text-ink-muted">
              {user?.email}
            </div>
          </div>
        </div>

        <HeaderIconButton
          label={labels.logout}
          onClick={logout}
          className="hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/50 dark:hover:text-red-400"
        >
          <LogoutIcon className={iconClass} />
        </HeaderIconButton>
      </div>
    </header>
  );
}
